import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { searchItems } from "@/lib/search";
import { inr } from "@/lib/format";
import Modal, { inputCls, btnPrimary, btnGhost } from "@/components/Modal";
import PaymentModal from "@/components/PaymentModal";
import { ThermalInvoice, A4Invoice } from "@/components/InvoiceView";
import {
  Search, Leaf, Drumstick, Trash2, Plus, Minus, Save, Printer,
  CreditCard, FileText, Utensils, ShoppingBag, Bike, X,
} from "lucide-react";

const ORDER_TYPES = [
  { id: "DINE_IN", label: "Dine In", icon: Utensils },
  { id: "TAKEAWAY", label: "Takeaway", icon: ShoppingBag },
  { id: "DELIVERY", label: "Delivery", icon: Bike },
];

export default function POS() {
  const { user } = useAuth();
  const { menu, categories, settings, loadMenu } = useData();
  const [params] = useSearchParams();

  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("ALL");
  const [results, setResults] = useState([]);
  const [selIdx, setSelIdx] = useState(0);
  const [cart, setCart] = useState([]);
  const [selRow, setSelRow] = useState(-1);
  const [orderType, setOrderType] = useState("DINE_IN");
  const [tableId, setTableId] = useState("");
  const [tables, setTables] = useState([]);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [varItem, setVarItem] = useState(null);
  const [printBill, setPrintBill] = useState(null);
  const [printFormat, setPrintFormat] = useState("thermal");

  const searchRef = useRef(null);
  const resultRefs = useRef([]);

  const allowDiscount = user?.role === "admin" || settings?.allow_cashier_discount;

  const loadTables = useCallback(() => {
    api.get("/tables").then((r) => setTables(r.data));
  }, []);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // init from URL
  useEffect(() => {
    const t = params.get("table");
    const b = params.get("bill");
    if (t) {
      setOrderType("DINE_IN");
      setTableId(t);
    }
    if (b) {
      api.get(`/bills/${b}`).then(({ data }) => {
        setCart(data.items.map((i) => ({ ...i })));
        setOrderType(data.order_type);
        setTableId(data.table_id || "");
        setCustomer({ name: data.customer_name || "", phone: data.customer_phone || "", address: data.customer_address || "" });
      }).catch(() => {});
    }
  }, [params]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // live search
  useEffect(() => {
    let list = menu.filter((i) => i.available);
    if (cat !== "ALL") list = list.filter((i) => i.category_id === cat);
    const r = query.trim() ? searchItems(list, query) : (query === "" && cat !== "ALL" ? list : []);
    setResults(r.slice(0, 40));
    setSelIdx(0);
  }, [query, cat, menu]);

  const addToCart = (item, variation = null, rate = null) => {
    const useRate = rate != null ? rate : item.price;
    const varName = variation;
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.item_id === item.id && c.variation === varName);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1, amount: (next[idx].qty + 1) * next[idx].rate };
        return next;
      }
      return [
        ...prev,
        { item_id: item.id, name: item.name, qty: 1, rate: useRate, gst_percent: item.gst_percent, variation: varName, amount: useRate },
      ];
    });
    toast.success(`${item.name}${varName ? " (" + varName + ")" : ""} added`, { duration: 900 });
  };

  const selectResult = (item) => {
    if (item.variations && item.variations.length > 0) {
      setVarItem(item);
      return;
    }
    addToCart(item);
    setQuery("");
    setResults([]);
    searchRef.current?.focus();
  };

  const pickVariation = (v) => {
    if (!varItem) return;
    addToCart(varItem, v.name, v.price);
    setVarItem(null);
    setQuery("");
    setResults([]);
    searchRef.current?.focus();
  };

  const changeQty = (idx, delta) => {
    setCart((prev) => {
      const next = [...prev];
      const q = Math.max(0, next[idx].qty + delta);
      if (q === 0) return next.filter((_, i) => i !== idx);
      next[idx] = { ...next[idx], qty: q, amount: q * next[idx].rate };
      return next;
    });
  };

  const setQty = (idx, q) => {
    const qty = Math.max(1, parseInt(q) || 1);
    setCart((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], qty, amount: qty * next[idx].rate };
      return next;
    });
  };

  const removeRow = (idx) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
    setSelRow(-1);
  };

  const newBill = useCallback(() => {
    setCart([]);
    setQuery("");
    setResults([]);
    setDiscountType("none");
    setDiscountValue(0);
    setCustomer({ name: "", phone: "", address: "" });
    setDeliveryCharge(0);
    setTableId("");
    setSelRow(-1);
    searchRef.current?.focus();
  }, []);

  // totals
  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, i) => s + i.qty * i.rate, 0);
    let discountAmount = 0;
    if (discountType === "percent") discountAmount = (subtotal * (parseFloat(discountValue) || 0)) / 100;
    else if (discountType === "fixed") discountAmount = Math.min(parseFloat(discountValue) || 0, subtotal);
    const taxable = subtotal - discountAmount;
    const ratio = subtotal > 0 ? taxable / subtotal : 0;
    let tax = 0;
    cart.forEach((i) => { tax += i.qty * i.rate * ratio * (i.gst_percent / 100); });
    const taxType = settings?.tax_type || "cgst_sgst";
    const cgst = taxType === "igst" ? 0 : tax / 2;
    const sgst = taxType === "igst" ? 0 : tax - cgst;
    const igst = taxType === "igst" ? tax : 0;
    const grand = taxable + tax + (parseFloat(deliveryCharge) || 0);
    return { subtotal, discountAmount, taxable, tax, cgst, sgst, igst, grand, taxType };
  }, [cart, discountType, discountValue, deliveryCharge, settings]);

  const buildPayload = (status, payment = null) => ({
    order_type: orderType,
    table_id: orderType === "DINE_IN" ? tableId || null : null,
    customer_name: customer.name || null,
    customer_phone: customer.phone || null,
    customer_address: customer.address || null,
    items: cart,
    discount_type: discountType,
    discount_value: parseFloat(discountValue) || 0,
    delivery_charge: orderType === "DELIVERY" ? parseFloat(deliveryCharge) || 0 : 0,
    payment,
    status,
  });

  const saveBill = async (status, payment = null) => {
    if (cart.length === 0) return toast.error("Cart is empty");
    try {
      const { data } = await api.post("/bills", buildPayload(status, payment));
      loadTables();
      return data;
    } catch (e) {
      toast.error(apiErr(e));
      throw e;
    }
  };

  const onSave = async () => {
    try {
      await saveBill("held");
      toast.success("Bill saved (held)");
      newBill();
    } catch {}
  };

  const onPrintCurrent = async (format = "thermal") => {
    try {
      const bill = await saveBill("held");
      setPrintFormat(format);
      setPrintBill({ ...bill });
      setTimeout(() => window.print(), 150);
    } catch {}
  };

  const onPaymentConfirm = async (payment) => {
    try {
      const bill = await saveBill("paid", payment);
      setPayOpen(false);
      toast.success(`Paid · ${bill.invoice_no}`);
      setPrintFormat("thermal");
      setPrintBill({ ...bill });
      setTimeout(() => {
        window.print();
        newBill();
      }, 150);
    } catch {}
  };

  // keyboard shortcuts
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      return el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
    };
    const onKey = (e) => {
      if (payOpen || varItem) return;
      if (e.key === "F2") { e.preventDefault(); searchRef.current?.focus(); searchRef.current?.select(); return; }
      if (e.key === "F4") { e.preventDefault(); newBill(); return; }
      if (e.key === "F8" || e.key === "F9") { e.preventDefault(); if (cart.length) setPayOpen(true); return; }
      if (e.key === "F10") { e.preventDefault(); if (cart.length) onPrintCurrent("thermal"); return; }
      if (!isTyping()) {
        if ((e.key === "+" || e.key === "*") && selRow >= 0) { e.preventDefault(); changeQty(selRow, 1); }
        else if (e.key === "-" && selRow >= 0) { e.preventDefault(); changeQty(selRow, -1); }
        else if (e.key === "Delete" && selRow >= 0) { e.preventDefault(); removeRow(selRow); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [payOpen, varItem, cart.length, selRow, newBill]);

  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[selIdx]) selectResult(results[selIdx]); }
    else if (e.key === "Escape") { setQuery(""); setResults([]); }
  };

  useEffect(() => {
    resultRefs.current[selIdx]?.scrollIntoView({ block: "nearest" });
  }, [selIdx]);

  const popular = useMemo(() => menu.filter((i) => i.available).slice(0, 10), [menu]);

  return (
    <div className="h-screen flex flex-col">
      {/* top bar */}
      <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-[#23334D] bg-[#111827]">
        <div className="flex items-center gap-2">
          {ORDER_TYPES.map((o) => (
            <button
              key={o.id}
              data-testid={`order-type-${o.id}`}
              onClick={() => setOrderType(o.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                orderType === o.id ? "bg-[#10B981] text-[#06221a]" : "bg-[#162032] text-[#9CA3AF] hover:text-white"
              }`}
            >
              <o.icon className="w-4 h-4" /> {o.label}
            </button>
          ))}
          {orderType === "DINE_IN" && (
            <select className={inputCls + " w-auto ml-2"} value={tableId} data-testid="table-select" onChange={(e) => setTableId(e.target.value)}>
              <option value="">No table</option>
              {tables.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
        </div>
        <div className="hidden lg:flex items-center gap-3 text-[11px] text-[#6B7280]">
          <span><span className="kbd">F2</span> Search</span>
          <span><span className="kbd">F4</span> New</span>
          <span><span className="kbd">F8</span> Pay</span>
          <span><span className="kbd">F10</span> Print</span>
          <span><span className="kbd">Del</span> Remove</span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 min-h-0">
        {/* LEFT */}
        <div className="lg:col-span-7 flex flex-col min-h-0 border-r border-[#23334D]">
          <div className="p-4 relative">
            <Search className="w-5 h-5 text-[#6B7280] absolute left-7 top-1/2 -translate-y-1/2" />
            <input
              ref={searchRef}
              data-testid="pos-item-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Type item name, initials or code…"
              className="w-full bg-[#162032] border-2 border-[#23334D] focus:border-[#10B981] rounded-xl pl-11 pr-4 py-3.5 text-lg text-white outline-none"
            />
            {query.trim() && results.length > 0 && (
              <div className="absolute left-4 right-4 top-[72px] z-30 bg-[#162032] border border-[#23334D] rounded-xl shadow-2xl max-h-[46vh] overflow-y-auto">
                {results.map((it, i) => (
                  <div
                    key={it.id}
                    ref={(el) => (resultRefs.current[i] = el)}
                    data-testid={`search-result-${it.id}`}
                    onMouseEnter={() => setSelIdx(i)}
                    onClick={() => selectResult(it)}
                    className={`flex items-center justify-between px-4 py-2.5 cursor-pointer ${i === selIdx ? "bg-[#10B981]/15 border-l-2 border-[#10B981]" : "border-l-2 border-transparent"}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {it.veg ? <Leaf className="w-3.5 h-3.5 text-[#10B981] shrink-0" /> : <Drumstick className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{it.name}{it.variations?.length > 0 && <span className="text-[#06B6D4] text-xs ml-2">{it.variations.length} sizes</span>}</div>
                        <div className="text-xs text-[#6B7280]">{it.short_code} · {it.category_name}</div>
                      </div>
                    </div>
                    <span className="font-mono font-semibold shrink-0 ml-3">{inr(it.price)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* categories */}
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            <button onClick={() => setCat("ALL")} data-testid="cat-ALL" className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${cat === "ALL" ? "bg-[#F59E0B] text-[#241a02]" : "bg-[#162032] text-[#9CA3AF] hover:text-white"}`}>ALL</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`cat-${c.id}`} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${cat === c.id ? "bg-[#F59E0B] text-[#241a02]" : "bg-[#162032] text-[#9CA3AF] hover:text-white"}`}>{c.name.toUpperCase()}</button>
            ))}
          </div>

          {/* grid of items */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="text-xs text-[#6B7280] uppercase tracking-wide font-semibold mb-2">{query || cat !== "ALL" ? "Results" : "Popular Items"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {(query || cat !== "ALL" ? results : popular).map((it) => (
                <button
                  key={it.id}
                  data-testid={`grid-item-${it.id}`}
                  onClick={() => selectResult(it)}
                  className="text-left bg-[#162032] border border-[#23334D] hover:border-[#10B981] rounded-xl p-3 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    {it.veg ? <Leaf className="w-3.5 h-3.5 text-[#10B981]" /> : <Drumstick className="w-3.5 h-3.5 text-[#EF4444]" />}
                    <span className="font-mono font-bold text-sm">{inr(it.price)}</span>
                  </div>
                  <div className="font-medium text-sm mt-2 line-clamp-2 leading-snug">{it.name}</div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">{it.short_code}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — current bill */}
        <div className="lg:col-span-5 flex flex-col min-h-0 bg-[#111827]">
          {orderType === "DELIVERY" && (
            <div className="p-3 border-b border-[#23334D] grid grid-cols-2 gap-2">
              <input className={inputCls} placeholder="Customer name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
              <input className={inputCls} placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
              <input className={inputCls + " col-span-2"} placeholder="Address" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} />
              <input type="number" className={inputCls + " col-span-2"} placeholder="Delivery charge ₹" value={deliveryCharge || ""} onChange={(e) => setDeliveryCharge(e.target.value)} />
            </div>
          )}

          <div className="px-4 py-2.5 grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide border-b border-[#23334D]">
            <div>Item</div><div className="w-24 text-center">Qty</div><div className="w-16 text-right">Rate</div><div className="w-20 text-right">Amount</div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-[#6B7280] gap-2">
                <FileText className="w-10 h-10 opacity-40" />
                <p className="text-sm">No items yet. Start typing to search.</p>
              </div>
            )}
            {cart.map((it, idx) => (
              <div
                key={idx}
                data-testid={`cart-row-${idx}`}
                onClick={() => setSelRow(idx)}
                className={`animate-row px-4 py-2.5 grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center border-b border-[#23334D]/60 cursor-pointer ${selRow === idx ? "bg-[#10B981]/10" : "hover:bg-[#162032]"}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{it.name}</div>
                  {it.variation && <div className="text-[11px] text-[#06B6D4]">{it.variation}</div>}
                </div>
                <div className="w-24 flex items-center justify-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); changeQty(idx, -1); }} className="w-6 h-6 rounded bg-[#1E2C45] flex items-center justify-center hover:bg-[#263657]"><Minus className="w-3 h-3" /></button>
                  <input
                    type="number"
                    value={it.qty}
                    data-testid={`cart-qty-${idx}`}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setQty(idx, e.target.value)}
                    className="w-10 bg-[#0B0F17] border border-[#23334D] rounded text-center text-sm py-1 outline-none focus:border-[#10B981] font-mono"
                  />
                  <button onClick={(e) => { e.stopPropagation(); changeQty(idx, 1); }} className="w-6 h-6 rounded bg-[#1E2C45] flex items-center justify-center hover:bg-[#263657]"><Plus className="w-3 h-3" /></button>
                </div>
                <div className="w-16 text-right font-mono text-sm text-[#9CA3AF]">{it.rate}</div>
                <div className="w-20 text-right font-mono text-sm font-semibold flex items-center justify-end gap-1">
                  {inr(it.amount)}
                  <button onClick={(e) => { e.stopPropagation(); removeRow(idx); }} data-testid={`remove-row-${idx}`} className="text-[#6B7280] hover:text-[#EF4444] ml-1"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>

          {/* totals */}
          <div className="border-t border-[#23334D] p-4 space-y-2 bg-[#0B0F17]">
            <div className="flex items-center gap-2">
              <select
                disabled={!allowDiscount}
                className={inputCls + " w-auto py-1.5 text-xs disabled:opacity-50"}
                value={discountType}
                data-testid="discount-type"
                onChange={(e) => setDiscountType(e.target.value)}
              >
                <option value="none">No Discount</option>
                <option value="percent">Discount %</option>
                <option value="fixed">Discount ₹</option>
              </select>
              {discountType !== "none" && allowDiscount && (
                <input type="number" className={inputCls + " w-24 py-1.5"} value={discountValue} data-testid="discount-value" onChange={(e) => setDiscountValue(e.target.value)} placeholder="0" />
              )}
            </div>

            <Row l="Subtotal" r={inr(totals.subtotal)} />
            {totals.discountAmount > 0 && <Row l="Discount" r={"-" + inr(totals.discountAmount)} accent="#EF4444" />}
            <Row l="Taxable" r={inr(totals.taxable)} />
            {totals.taxType === "igst" ? (
              <Row l={`IGST`} r={inr(totals.igst)} />
            ) : (
              <>
                <Row l="CGST" r={inr(totals.cgst)} />
                <Row l="SGST" r={inr(totals.sgst)} />
              </>
            )}
            {orderType === "DELIVERY" && parseFloat(deliveryCharge) > 0 && <Row l="Delivery" r={inr(parseFloat(deliveryCharge))} />}

            <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#23334D]">
              <span className="font-head font-bold text-lg">GRAND TOTAL</span>
              <span className="font-head font-bold text-3xl font-mono text-[#10B981]" data-testid="grand-total">{inr(totals.grand)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <button onClick={onSave} data-testid="save-bill-btn" className={btnGhost + " flex items-center justify-center gap-2"}><Save className="w-4 h-4" /> Save</button>
              <button onClick={() => onPrintCurrent("thermal")} data-testid="print-bill-btn" className={btnGhost + " flex items-center justify-center gap-2"}><Printer className="w-4 h-4" /> Print</button>
              <button onClick={newBill} data-testid="new-bill-btn" className={btnGhost + " flex items-center justify-center gap-2"}><FileText className="w-4 h-4" /> New</button>
              <button onClick={() => cart.length && setPayOpen(true)} data-testid="payment-btn" className={btnPrimary + " flex items-center justify-center gap-2"}><CreditCard className="w-4 h-4" /> Payment</button>
            </div>
          </div>
        </div>
      </div>

      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} total={totals.grand} onConfirm={onPaymentConfirm} />

      <Modal open={!!varItem} onClose={() => setVarItem(null)} title={`Select size · ${varItem?.name || ""}`} width="max-w-sm">
        <div className="space-y-2">
          {varItem?.variations.map((v, i) => (
            <button key={i} data-testid={`variation-${i}`} onClick={() => pickVariation(v)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-[#0B0F17] border border-[#23334D] hover:border-[#10B981]">
              <span className="font-medium">{v.name}</span>
              <span className="font-mono font-semibold">{inr(v.price)}</span>
            </button>
          ))}
        </div>
      </Modal>

      {printBill && printFormat === "thermal" && <ThermalInvoice bill={printBill} settings={settings} />}
      {printBill && printFormat === "a4" && <A4Invoice bill={printBill} settings={settings} />}
    </div>
  );
}

const Row = ({ l, r, accent }) => (
  <div className="flex justify-between text-sm">
    <span className="text-[#9CA3AF]">{l}</span>
    <span className="font-mono" style={accent ? { color: accent } : {}}>{r}</span>
  </div>
);
